import { Building2, Crown, ShieldCog, type LucideIcon } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";

/** owner: real OWNER membership. member: ADMIN or AGENT. platform: reachable only through Platform Admin access. */
export type OrganizationAccessKind = "owner" | "member" | "platform";

/** `role` is the real membership role; null means Platform Admin access without a membership. */
export function organizationAccessKind(role: OrganizationMemberRoleName | null): OrganizationAccessKind {
  if (role === null) return "platform";
  return role === "OWNER" ? "owner" : "member";
}

const ACCESS_ICONS: Record<OrganizationAccessKind, LucideIcon> = {
  owner: Crown,
  member: Building2,
  platform: ShieldCog,
};

/** Crown when owned, ShieldCog when system-managed, Building2 for administered and member access. */
export function organizationIconForRole(role: OrganizationMemberRoleName | null): LucideIcon {
  return ACCESS_ICONS[organizationAccessKind(role)];
}

/** Well colors on white surfaces (switcher menu, workspace chooser). */
export const ORGANIZATION_ICON_TONE: Record<OrganizationAccessKind, string> = {
  owner: "border-transparent bg-brand-mint text-brand-primary",
  member: "border-transparent bg-[#eff4f2] text-[#4f7266]",
  platform: "border-[#d3e7e4] bg-[#e9f4f3] text-[#2a7169]",
};

/** Dedicated Super Admin destination on white surfaces: console charcoal with a mint shield. */
export const SUPER_ADMIN_ICON_TONE = "border-transparent bg-[#1c2b2b] text-[#7fe0c0]";

/** Uniform organization / workspace mark — not name initials. */
export function OrganizationIcon({
  size = "md",
  className,
  iconClassName,
  icon: Icon = Building2,
  iconSize: iconSizeOverride,
}: {
  size?: "sm" | "md" | "lg";
  className?: string;
  iconClassName?: string;
  /** Shield for the Super Admin workspace; Building2 for organizations. */
  icon?: LucideIcon;
  iconSize?: number;
}): React.JSX.Element {
  const box =
    size === "sm" ? "size-7 rounded-md" : size === "lg" ? "size-10 rounded-lg" : "size-8 rounded-md";
  const iconSize = iconSizeOverride ?? (size === "sm" ? 14 : size === "lg" ? 18 : 16);

  return (
    <span
      className={cn(
        "inline-grid shrink-0 place-items-center border border-line bg-surface-muted text-muted",
        box,
        className,
      )}
      aria-hidden="true"
      data-testid="organization-icon"
    >
      <Icon size={iconSize} className={iconClassName} aria-hidden="true" />
    </span>
  );
}
